import { Trash2, CheckCircle2, CircleDollarSign } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATUS = ["new", "contacted", "booked", "tested", "cancelled"];
const COLORS = { new: "bg-[#FF2A8D]", contacted: "bg-[#FFB800]", booked: "bg-[#125AA0]", tested: "bg-[#10B981]", cancelled: "bg-slate-500" };
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");

export const RebateBadge = ({ r, onRebate }) => {
  if (r.rebate_paid) {
    return (
      <button data-testid={`rebate-toggle-${r.id}`} onClick={() => onRebate(r.id, false)} title="Click to mark as not paid"
        className="inline-flex items-center gap-1.5 rounded-full bg-[#10B981]/15 border border-[#10B981]/40 text-[#34D399] px-3 py-1.5 text-xs font-semibold whitespace-nowrap">
        <CheckCircle2 size={14} /> $10 rebate paid {fmt(r.rebate_paid_at)}
      </button>
    );
  }
  const owed = r.status === "tested";
  return (
    <button data-testid={`rebate-toggle-${r.id}`} onClick={() => onRebate(r.id, true)}
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold whitespace-nowrap transition-colors ${owed ? "bg-[#FFB800]/15 border border-[#FFB800]/50 text-[#FFD466] hover:bg-[#FFB800]/25" : "border border-white/15 text-slate-300 hover:border-white/40"}`}>
      <CircleDollarSign size={14} /> {owed ? "$10 rebate owed · Mark paid" : "Mark $10 rebate paid"}
    </button>
  );
};

export const RequestsTable = ({ rows, onStatus, onDelete, onRebate, empty = "No test requests yet." }) => {
  if (!rows.length) return <p data-testid="requests-empty" className="text-slate-400 py-16 text-center">{empty}</p>;
  return (
    <div className="space-y-3">
      {rows.map((r) => (
        <div key={r.id} data-testid={`request-row-${r.id}`} className="rounded-2xl bg-white/[0.04] border border-white/10 p-4 sm:p-5 grid gap-4 md:grid-cols-12 md:items-center">
          <div className="md:col-span-4 min-w-0">
            <p className="font-semibold truncate">{r.name}</p>
            <a href={`mailto:${r.email}`} className="text-sm text-[#5B95D6] break-all hover:underline">{r.email}</a>
            <a href={`tel:${r.phone}`} className="block text-sm text-slate-300 hover:underline">{r.phone}</a>
          </div>
          <div className="md:col-span-4 text-sm text-slate-300 grid grid-cols-2 gap-x-4 gap-y-1">
            <span className="text-slate-400">City</span><span>{r.city}</span>
            <span className="text-slate-400">Wants</span><span>{fmt(r.preferred_date)}</span>
            <span className="text-slate-400">Type</span><span className="capitalize">{(r.package || "").replace("-", " ")}</span>
            <span className="text-slate-400">Sent</span><span>{fmt(r.created_at)}</span>
            <span className="text-slate-400">Coupon</span><span data-testid={`request-coupon-${r.id}`} className="text-[#FF2A8D]">{r.coupon || "—"}</span>
          </div>
          <div className="md:col-span-4 flex flex-wrap items-center gap-2 md:justify-end">
            <RebateBadge r={r} onRebate={onRebate} />
            <Select value={r.status || "new"} onValueChange={(v) => onStatus(r.id, v)}>
              <SelectTrigger data-testid={`request-status-${r.id}`} className="w-40 rounded-full bg-white/5 border-white/10 capitalize">
                <span className={`w-2 h-2 rounded-full mr-2 ${COLORS[r.status] || COLORS.new}`} /><SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0E1326] border-white/10 text-white">
                {STATUS.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <button data-testid={`request-delete-${r.id}`} onClick={() => onDelete(r.id)} aria-label="Delete request"
              className="p-2.5 rounded-full hover:bg-red-500/15 text-slate-400 hover:text-red-400 transition-colors"><Trash2 size={18} /></button>
          </div>
        </div>
      ))}
    </div>
  );
};

export const WaitlistTable = ({ rows, onDelete }) => {
  if (!rows.length) return <p data-testid="waitlist-empty" className="text-slate-400 py-16 text-center">No waitlist sign-ups yet.</p>;
  return (
    <div className="rounded-2xl border border-white/10 divide-y divide-white/10">
      {rows.map((w) => (
        <div key={w.id} data-testid={`waitlist-row-${w.id}`} className="flex items-center gap-3 px-4 sm:px-5 py-3.5">
          <div className="flex-1 min-w-0">
            <a href={`mailto:${w.email}`} className="block truncate hover:underline">{w.email}</a>
            <p className="text-xs text-slate-400 capitalize">{w.role} · joined {fmt(w.created_at)}</p>
          </div>
          <button data-testid={`waitlist-delete-${w.id}`} onClick={() => onDelete(w.id)} aria-label="Remove sign-up"
            className="p-2.5 rounded-full hover:bg-red-500/15 text-slate-400 hover:text-red-400 transition-colors"><Trash2 size={18} /></button>
        </div>
      ))}
    </div>
  );
};

export const toCsv = (rows, cols) =>
  [cols.join(","), ...rows.map((r) => cols.map((c) => `"${String(r[c] ?? "").replace(/"/g, '""')}"`).join(","))].join("\n");
